/**
 * Resolve relative media / upload paths against the API origin.
 * Handles absolute URLs, data URIs, `/uploads/…`, `/images/…`, and bare keys.
 */
export function getApiOrigin(): string {
  const env = (
    import.meta as ImportMeta & { env?: { VITE_API_BASE_URL?: string } }
  ).env;
  const apiBase = env?.VITE_API_BASE_URL || "http://localhost:4000/api/";
  try {
    return new URL(apiBase, window.location.href).origin;
  } catch {
    return window.location.origin;
  }
}

export const IMAGE_TRANSFORM_QUALITY = 80;

export type AssetUrlTransform = {
  width?: number;
  height?: number;
  quality?: number;
};

export function resolveAssetUrl(
  url: string | null | undefined,
  transform?: AssetUrlTransform,
): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  let resolved: string;
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("data:")) {
    resolved = trimmed;
  } else if (trimmed.startsWith("//")) {
    resolved = `${window.location.protocol}${trimmed}`;
  } else {
    try {
      resolved = new URL(trimmed, getApiOrigin()).toString();
    } catch {
      resolved = trimmed;
    }
  }
  if (!hasAssetTransform(transform)) return resolved;
  return applyImageKitTransform(resolved, transform);
}

function hasAssetTransform(
  transform?: AssetUrlTransform,
): transform is AssetUrlTransform {
  return Boolean(
    transform &&
      ((transform.width != null && transform.width > 0) ||
        (transform.height != null && transform.height > 0)),
  );
}

function applyImageKitTransform(
  url: string,
  transform: AssetUrlTransform,
): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (!parsed.hostname.includes("imagekit.io")) return url;
  if (parsed.searchParams.has("tr")) return url;
  const parts: string[] = [];
  if (transform.width != null && transform.width > 0) {
    parts.push(`w-${Math.round(transform.width)}`);
  }
  if (transform.height != null && transform.height > 0) {
    parts.push(`h-${Math.round(transform.height)}`);
  }
  parts.push(
    `q-${transform.quality ?? IMAGE_TRANSFORM_QUALITY}`,
    "f-auto",
    "c-at_max",
  );
  const tr = parts.join(",");
  const hash = parsed.hash;
  const withoutHash = hash ? url.slice(0, url.length - hash.length) : url;
  const separator = parsed.search ? "&" : "?";
  return `${withoutHash}${separator}tr=${tr}${hash}`;
}
